import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, getErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import PushNotificationToggle from "@/components/worker/PushNotificationToggle";
import {
  Camera,
  IdentificationCard,
  FileText,
  Signature,
  CheckCircle,
  UserCircle,
  UploadSimple,
  Warning,
  Car,
  Truck,
  AddressBook,
  ListChecks,
  CalendarBlank,
  TShirt,
  CurrencyDollar,
  SealCheck,
  CaretRight,
  ChatText,
} from "@phosphor-icons/react";

// Pretty labels for the profile-complete checklist
const FIELD_LABELS = {
  phone: "Phone number",
  zip_code: "ZIP code",
  date_of_birth: "Date of birth",
  skills: "Work skills — complete the questionnaire",
  specialist_trade_incomplete: "Finish or remove an incomplete specialist trade",
  availability: "Availability (pick at least one)",
  emergency_contact_name: "Emergency contact name",
  emergency_contact_phone: "Emergency contact phone",
};

const AVAILABILITY_LABELS = {
  weekdays: "Weekdays",
  weekends: "Weekends",
  mornings: "Mornings",
  evenings: "Evenings",
  overnight: "Overnight",
  full_time: "Full-time",
};

const EXPERIENCE_LABELS = {
  none: "No experience",
  "0_1_yr": "Under 1 year",
  "1_3_yr": "1–3 years",
  "3_plus_yr": "3+ years",
};

export default function WorkerProfile() {
  const { user, checkAuth } = useAuth();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const avatarInput = useRef(null);
  const idInput = useRef(null);
  const w9Input = useRef(null);
  const agreementInput = useRef(null);

  // Load enum options
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/options");
        setOptions(data);
      } catch (e) {
        toast.error(getErr(e));
      }
    })();
  }, []);

  // Initialize form from user
  useEffect(() => {
    if (user && !form) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        zip_code: user.zip_code || "",
        city: user.city || "",
        state: user.state || "",
        date_of_birth: user.date_of_birth || "",
        has_car: !!user.has_car,
        has_truck: !!user.has_truck,
        availability: user.availability || [],
        emergency_contact_name: user.emergency_contact_name || "",
        emergency_contact_phone: user.emergency_contact_phone || "",
        tshirt_size: user.tshirt_size || "",
        payout_method: user.payout_method || "",
        payout_handle: user.payout_handle || "",
        bio: user.bio || "",
      });
    }
  }, [user, form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) =>
    setForm((f) => {
      const arr = f[k] || [];
      return {
        ...f,
        [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v],
      };
    });

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      await checkAuth();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(getErr(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await checkAuth();
      toast.success("Photo updated");
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const uploadId = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/id", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await checkAuth();
      toast.success("ID uploaded — pending HCOB verification");
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  // W9 and signed agreement — same shape as ID upload but PDFs are allowed
  // and we hit different endpoints. The user's document flips *_verified
  // back to false on every replacement so admins re-review.
  const uploadDoc = async (endpoint, successMsg, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await checkAuth();
      toast.success(successMsg);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  const uploadW9 = (f) => uploadDoc("/profile/w9", "W-9 uploaded — pending review", f);
  const uploadAgreement = (f) =>
    uploadDoc("/profile/agreement", "Agreement uploaded — pending review", f);

  const missing = user?.profile_missing_fields || [];
  const needsId = !user?.id_image_path;
  const idPending = !!user?.id_image_path && !user?.id_verified;
  const totalChecks = (user?.profile_missing_fields ? Object.keys(FIELD_LABELS).length : 0) + 1;
  const doneChecks = totalChecks - missing.length - (needsId ? 1 : 0);
  const progressPct = Math.round((doneChecks / Math.max(totalChecks, 1)) * 100);

  if (!user || !form || !options) return null;

  return (
    <div className="px-5 py-6 pb-32" data-testid="worker-profile">
      <div className="font-mono-label">My profile</div>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">{user.name}</h1>

      {/* Completion banner */}
      <CompletionBanner
        missing={missing}
        needsId={needsId}
        idPending={idPending}
        idVerified={!!user.id_verified}
        progressPct={progressPct}
      />

      {/* Push notifications — high in the flow so workers see it on first
          profile visit. Gracefully degrades to PWA-install hints on iOS. */}
      <div className="mt-6">
        <PushNotificationToggle />
      </div>

      {/* Avatar + ID */}
      <div className="mt-6 gb-tactile rounded-2xl border border-black/5 bg-white p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-[#F0F4FF] text-[#0044FF]">
              {user.avatar_path ? (
                <ProtectedImg path={user.avatar_path} />
              ) : (
                <UserCircle size={50} weight="duotone" />
              )}
            </div>
            <button
              data-testid="upload-avatar-btn"
              onClick={() => avatarInput.current?.click()}
              className="absolute -bottom-2 -right-2 grid h-10 w-10 place-items-center rounded-full bg-[#030712] text-white"
            >
              <Camera size={16} weight="fill" />
            </button>
            <input
              ref={avatarInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
            />
          </div>
          <div>
            <div className="font-display text-lg font-bold">{user.name}</div>
            <div className="text-xs text-[#4B5563]">{user.email}</div>
          </div>
        </div>

        <div className="mt-5 border-t border-[#E5E7EB] pt-4">
          <div className="font-mono-label mb-2 flex items-center gap-2">
            <IdentificationCard size={14} weight="duotone" /> Government ID
          </div>
          {user.id_image_path ? (
            <div>
              <div className="overflow-hidden rounded-xl border border-[#E5E7EB]">
                <ProtectedImg path={user.id_image_path} className="w-full" />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                {user.id_verified ? (
                  <span className="inline-flex items-center gap-1 text-[#10B981]">
                    <CheckCircle size={12} weight="fill" /> Verified
                  </span>
                ) : (
                  <span className="text-[#F59E0B] font-semibold">Pending verification</span>
                )}
                <button
                  data-testid="replace-id-btn"
                  onClick={() => idInput.current?.click()}
                  className="inline-flex min-h-[40px] items-center px-2 font-semibold text-[#0044FF]"
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="upload-id-dropzone"
              onClick={() => idInput.current?.click()}
              className="gb-dropzone flex w-full flex-col items-center justify-center rounded-xl bg-white p-6 text-center"
            >
              <UploadSimple size={28} weight="duotone" className="text-[#0044FF]" />
              <div className="mt-2 text-sm font-semibold">Upload a photo of your ID</div>
              <div className="mt-1 text-xs text-[#4B5563]">JPG, PNG · 1 image</div>
            </button>
          )}
          <input
            ref={idInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadId(e.target.files?.[0])}
          />
        </div>
      </div>

      <form onSubmit={save} className="mt-6 space-y-6">
        {/* Section: Basics */}
        <Section title="Basics">
          <Field label="Full name" required>
            <Input
              data-testid="profile-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </Field>
          <Field label="Phone" required hint="+1 555 1234567">
            <Input
              data-testid="profile-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              required
            />
          </Field>
          <Field label="Date of birth" required>
            <Input
              data-testid="profile-dob"
              type="date"
              value={form.date_of_birth}
              onChange={(e) => set("date_of_birth", e.target.value)}
              required
            />
          </Field>
        </Section>

        {/* Section: Where you work */}
        <Section title="Where you work" icon={AddressBook}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ZIP code" required>
              <Input
                data-testid="profile-zip"
                value={form.zip_code}
                onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
                maxLength={5}
                inputMode="numeric"
                placeholder="94110"
                required
              />
            </Field>
            <Field label="City">
              <Input
                data-testid="profile-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </Field>
          </div>
          <Field label="State">
            <Input
              data-testid="profile-state"
              value={form.state}
              onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              placeholder="CA"
            />
          </Field>
          <Field label="Street address (optional)" hint="Not shared with other workers">
            <Input
              data-testid="profile-address"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
            />
          </Field>
        </Section>

        {/* Section: What you do — questionnaire v2 summary */}
        <WorkSummary user={user} options={options} />

        {/* Section: Availability */}
        <Section title="When you're free" icon={CalendarBlank} required>
          <div className="grid grid-cols-2 gap-2">
            {options.availability.map((a) => (
              <ChipToggle
                key={a}
                testId={`avail-${a}`}
                active={form.availability.includes(a)}
                onClick={() => toggleArr("availability", a)}
                label={AVAILABILITY_LABELS[a] || a}
              />
            ))}
          </div>
        </Section>

        {/* Section: Vehicle */}
        <Section title="Vehicle" icon={Car}>
          <p className="-mt-2 mb-2 text-xs text-[#4B5563]">
            Used for driver gigs and labor that needs hauling.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <ChipToggle
              testId="vehicle-car"
              active={form.has_car}
              onClick={() => set("has_car", !form.has_car)}
              label="Car"
              icon={Car}
            />
            <ChipToggle
              testId="vehicle-truck"
              active={form.has_truck}
              onClick={() => set("has_truck", !form.has_truck)}
              label="Truck"
              icon={Truck}
            />
          </div>
          <div className="mt-2 text-[11px] text-[#4B5563]">
            Have a CDL? It now lives under{" "}
            <Link to="/crew/certifications" className="font-semibold text-[#0044FF]">
              Certifications
            </Link>{" "}
            — upload your license there.
          </div>
        </Section>

        {/* Section: Emergency contact */}
        <Section title="Emergency contact" required>
          <Field label="Name" required>
            <Input
              data-testid="profile-ec-name"
              value={form.emergency_contact_name}
              onChange={(e) => set("emergency_contact_name", e.target.value)}
              required
            />
          </Field>
          <Field label="Phone" required>
            <Input
              data-testid="profile-ec-phone"
              value={form.emergency_contact_phone}
              onChange={(e) => set("emergency_contact_phone", e.target.value)}
              required
            />
          </Field>
        </Section>

        {/* Section: W-9 form */}
        <DocumentUploadCard
          title="W-9 Tax Form"
          hint="Upload a signed W-9. PDF, JPG or PNG."
          icon={FileText}
          path={user.w9_path}
          verified={!!user.w9_verified}
          uploadedAt={user.w9_uploaded_at}
          inputRef={w9Input}
          onFile={uploadW9}
          testidPrefix="w9"
        />

        {/* Section: Signed contractor agreement */}
        <DocumentUploadCard
          title="Signed Agreement"
          hint="Upload your signed HCOB contractor agreement. PDF, JPG or PNG."
          icon={Signature}
          path={user.agreement_path}
          verified={!!user.agreement_verified}
          uploadedAt={user.agreement_uploaded_at}
          inputRef={agreementInput}
          onFile={uploadAgreement}
          testidPrefix="agreement"
        />

        {/* Section: Text updates — SMS opt-in / opt-out (A2P 10DLC) */}
        <SmsOptInSection user={user} onChanged={checkAuth} />

        {/* Section: Payout — how HCOB sends you money */}
        <Section title="How we pay you" icon={CurrencyDollar}>
          <div className="text-xs leading-relaxed text-[#4B5563]">
            Pick ONE method. We pay every shift via Zelle, Apple Cash, or Chime — same identifier you&apos;d give a friend to send you money.
          </div>
          <Field label="Payout method">
            <select
              data-testid="profile-payout-method"
              value={form.payout_method}
              onChange={(e) => set("payout_method", e.target.value)}
              className="h-11 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm"
            >
              <option value="">Pick one…</option>
              <option value="zelle">Zelle</option>
              <option value="apple_cash">Apple Cash</option>
              <option value="chime">Chime</option>
            </select>
          </Field>
          {form.payout_method && (
            <Field
              label={
                form.payout_method === "zelle"
                  ? "Zelle (phone or email)"
                  : form.payout_method === "apple_cash"
                  ? "Apple Cash (phone number)"
                  : "Chime ($username or phone)"
              }
            >
              <Input
                data-testid="profile-payout-handle"
                value={form.payout_handle}
                onChange={(e) => set("payout_handle", e.target.value)}
                placeholder={
                  form.payout_method === "zelle"
                    ? "(410) 555-0123 or you@email.com"
                    : form.payout_method === "apple_cash"
                    ? "(410) 555-0123"
                    : "$YourChimeName"
                }
                inputMode={form.payout_method === "chime" ? "text" : "tel"}
              />
              <div className="mt-1 text-[11px] text-[#4B5563]">
                Double-check this — money goes here on payday.
              </div>
            </Field>
          )}
        </Section>

        {/* Section: Bio + gear */}
        <Section title="About you" icon={TShirt}>
          <Field label="T-shirt size">
            <select
              data-testid="profile-tshirt"
              value={form.tshirt_size}
              onChange={(e) => set("tshirt_size", e.target.value)}
              className="h-11 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm"
            >
              <option value="">Select…</option>
              {options.tshirt_sizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tell HCOB about yourself (optional)">
            <Textarea
              data-testid="profile-bio"
              rows={3}
              value={form.bio}
              onChange={(e) => set("bio", e.target.value)}
              placeholder="Your strengths, past work, anything you want HCOB to know…"
              className="rounded-xl border-[#E5E7EB]"
            />
          </Field>
        </Section>

        <Button
          data-testid="save-profile-btn"
          type="submit"
          disabled={saving}
          className="h-12 w-full rounded-2xl bg-[#030712] text-white"
        >
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </form>

      <Link
        to="/crew/certifications"
        data-testid="profile-certifications-link"
        className="mt-4 flex items-center gap-3 rounded-2xl border border-[#0044FF]/20 bg-[#F0F4FF] p-4 hover:bg-[#E0E9FF]"
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0044FF] text-white">
          <SealCheck size={20} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="font-display text-sm font-bold text-[#1D4ED8]">Professional Certifications</div>
          <div className="mt-0.5 text-xs text-[#1D4ED8]/80">
            Take tests & upload credentials to unlock specialty assignments.
          </div>
        </div>
        <CaretRight size={18} className="shrink-0 text-[#1D4ED8]" />
      </Link>

      <ChangePasswordCard />
    </div>
  );
}

const CLAIM_PILLS = {
  incomplete: ["#F59E0B", "INCOMPLETE"],
  pending: ["#0044FF", "PENDING VERIFICATION"],
  verified: ["#10B981", "VERIFIED"],
  returned: ["#EF4444", "RETURNED"],
};

function WorkSummary({ user, options }) {
  const skillLabel = Object.fromEntries((options.skills || []).map((s) => [s.value, s.label]));
  const attrLabel = Object.fromEntries((options.work_attributes || []).map((a) => [a.value, a.label]));
  const classes = user.work_classes || [];
  const generalSkills = user.general_skills || [];
  const trades = user.specialist_trades || [];
  const attrs = user.work_attributes || [];
  return (
    <section className="gb-tactile rounded-2xl border border-black/5 bg-white p-5" data-testid="work-profile-summary">
      <div className="mb-4 flex items-center gap-2">
        <ListChecks size={16} weight="duotone" className="text-[#0044FF]" />
        <h2 className="font-display text-lg font-black tracking-tight">What you do</h2>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Required</span>
      </div>

      {classes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {classes.map((c) => (
            <span key={c} className="rounded-full bg-[#030712] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
              {c === "general_labor" ? "Crew & Labor" : "Specialist"}
            </span>
          ))}
        </div>
      )}

      {generalSkills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {generalSkills.map((s) => (
            <span key={s} className="rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-2.5 py-1 text-[11px] font-semibold">
              {skillLabel[s] || s}
            </span>
          ))}
        </div>
      )}

      {trades.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {trades.map((t) => {
            const [bg, label] = CLAIM_PILLS[t.status] || CLAIM_PILLS.incomplete;
            return (
              <div key={t.trade} data-testid={`profile-trade-${t.trade}`} className="flex items-center justify-between rounded-xl border border-[#E5E7EB] px-3 py-2">
                <span className="text-sm font-bold">{skillLabel[t.trade] || t.trade.replace(/_/g, " ")}</span>
                <span className="rounded-full px-2 py-0.5 text-[9px] font-bold tracking-widest text-white" style={{ backgroundColor: bg }}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {attrs.length > 0 && (
        <div className="mt-3 text-xs text-[#4B5563]">
          Attributes: {attrs.map((a) => attrLabel[a] || a).join(", ")}
          {user.bilingual_languages && <> · {user.bilingual_languages}</>}
        </div>
      )}

      {classes.length === 0 && generalSkills.length === 0 && trades.length === 0 && (
        <div className="text-sm text-[#4B5563]">
          Tell HCOB how you work — crew gigs, a specialty trade, or both.
        </div>
      )}

      <Link
        to="/crew/questionnaire"
        data-testid="edit-questionnaire-link"
        className="mt-4 flex h-11 items-center justify-center rounded-xl bg-[#0044FF] text-sm font-bold text-white hover:bg-[#0036cc]"
      >
        {classes.length ? "Edit work questionnaire" : "Start work questionnaire"}
      </Link>
    </section>
  );
}

function CompletionBanner({ missing, needsId, idPending, idVerified, progressPct }) {
  const allDone = missing.length === 0 && idVerified;
  if (allDone) {
    return (
      <div
        data-testid="profile-complete-banner"
        className="mt-4 flex items-start gap-3 rounded-2xl border border-[#10B981]/30 bg-[#ECFDF5] p-4"
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#10B981] text-white">
          <CheckCircle size={20} weight="fill" />
        </div>
        <div>
          <div className="font-display text-base font-bold text-[#065F46]">
            You're all set — request away
          </div>
          <div className="mt-0.5 text-xs text-[#065F46]/80">
            Your profile is complete and HCOB verified your ID.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="profile-completion-banner"
      className="mt-4 rounded-2xl border border-[#F59E0B]/40 bg-[#FFFBEB] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#F59E0B] text-white">
          <Warning size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold text-[#92400E]">
            Finish your profile to request gigs
          </div>
          <div className="mt-0.5 text-xs text-[#92400E]/80">
            HCOB needs this info before you can claim an assignment.
          </div>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#F59E0B]/20">
        <div
          className="h-full bg-[#F59E0B] transition-all"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <ul className="mt-3 grid grid-cols-1 gap-1 text-xs text-[#92400E] sm:grid-cols-2">
        {(needsId || idPending) && (
          <li
            data-testid="missing-id"
            className="inline-flex items-center gap-1.5"
          >
            <span className="grid h-4 w-4 place-items-center rounded-full bg-[#F59E0B] text-white text-[10px] font-black">!</span>
            {needsId ? "Upload your ID" : "ID awaiting HCOB verification"}
          </li>
        )}
        {missing.map((m) => (
          <li
            key={m}
            data-testid={`missing-${m}`}
            className="inline-flex items-center gap-1.5"
          >
            <span className="grid h-4 w-4 place-items-center rounded-full bg-[#F59E0B] text-white text-[10px] font-black">!</span>
            {FIELD_LABELS[m] || m}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Worker-uploadable document with an admin verification lifecycle.
 * Renders the ID-style pattern for any single-file worker doc (W-9,
 * Agreement, etc). Accepts PDFs — for image files we show a thumbnail
 * via ProtectedImg, for PDFs we surface an "Open PDF" link that streams
 * with credentials. Uploading a replacement flips *_verified back to
 * false on the server, so the "Pending review" badge reappears until
 * admin re-verifies.
 */
function DocumentUploadCard({
  title,
  hint,
  icon: Icon,
  path,
  verified,
  uploadedAt,
  inputRef,
  onFile,
  testidPrefix,
}) {
  const isPdf = typeof path === "string" && path.toLowerCase().endsWith(".pdf");
  return (
    <Section title={title} icon={Icon}>
      <div className="text-xs text-[#4B5563]">{hint}</div>
      {path ? (
        <div className="mt-3">
          {isPdf ? (
            <a
              href={`${API}/files/${path}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testidPrefix}-open-pdf`}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm font-semibold text-[#0044FF] hover:bg-[#F0F4FF]"
            >
              <FileText size={16} weight="duotone" /> View uploaded PDF
            </a>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#E5E7EB]">
              <ProtectedImg path={path} className="w-full" />
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              {verified ? (
                <span
                  data-testid={`${testidPrefix}-verified-badge`}
                  className="inline-flex items-center gap-1 text-[#10B981]"
                >
                  <CheckCircle size={12} weight="fill" /> Verified
                </span>
              ) : (
                <span
                  data-testid={`${testidPrefix}-pending-badge`}
                  className="font-semibold text-[#F59E0B]"
                >
                  Pending review
                </span>
              )}
              {uploadedAt && (
                <span className="text-[10px] text-[#9CA3AF]">
                  uploaded {new Date(uploadedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <button
              type="button"
              data-testid={`${testidPrefix}-replace-btn`}
              onClick={() => inputRef.current?.click()}
              className="inline-flex min-h-[40px] items-center px-2 font-semibold text-[#0044FF]"
            >
              Replace
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testidPrefix}-upload-dropzone`}
          onClick={() => inputRef.current?.click()}
          className="gb-dropzone mt-2 flex w-full flex-col items-center justify-center rounded-xl bg-white p-6 text-center"
        >
          <UploadSimple size={28} weight="duotone" className="text-[#0044FF]" />
          <div className="mt-2 text-sm font-semibold">Upload {title}</div>
          <div className="mt-1 text-xs text-[#4B5563]">PDF, JPG, PNG · 15MB max</div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </Section>
  );
}


function Section({ title, icon: Icon, required, children }) {
  return (
    <section className="gb-tactile rounded-2xl border border-black/5 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        {Icon && <Icon size={16} weight="duotone" className="text-[#0044FF]" />}
        <h2 className="font-display text-lg font-black tracking-tight">{title}</h2>
        {required && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">
            Required
          </span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, required, hint, className = "", children }) {
  return (
    <div className={className}>
      <Label className="font-mono-label flex items-center gap-1">
        {label}
        {required && <span className="text-[#EF4444]">*</span>}
      </Label>
      <div className="mt-2 [&_input]:h-11 [&_input]:rounded-xl [&_input]:border-[#E5E7EB]">
        {children}
      </div>
      {hint && <div className="mt-1 text-[10px] text-[#4B5563]">{hint}</div>}
    </div>
  );
}

function ChipToggle({ active, onClick, label, icon: Icon, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex h-11 items-center justify-center gap-1.5 rounded-xl border text-sm font-bold ${
        active
          ? "border-[#0044FF] bg-[#0044FF] text-white"
          : "border-[#E5E7EB] bg-white text-[#030712] hover:border-[#0044FF]/30"
      }`}
    >
      {Icon && <Icon size={14} weight={active ? "fill" : "duotone"} />}
      {label}
    </button>
  );
}

function SmsOptInSection({ user, onChanged }) {
  const optedIn = !!user?.sms_opt_in;
  const phone = (user?.phone || "").trim();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!optedIn && !phone) {
      toast.error("Add a phone number to your profile first, then save.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/me/sms-opt-in", {
        opted_in: !optedIn,
        source: "worker_profile_toggle",
      });
      await onChanged?.();
      toast.success(!optedIn ? "You're in — we'll text you gig updates." : "You'll no longer get text updates.");
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Text updates" icon={ChatText}>
      <div className="flex items-start justify-between gap-4 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-bold text-[#030712]">
            Get texts about new gigs, offers, and reminders
          </div>
          <div className="mt-1 text-xs leading-relaxed text-[#4B5563]">
            Msg &amp; data rates may apply. Msg frequency varies. Reply <strong>STOP</strong> to opt out at any time, <strong>HELP</strong> for help.
            {" "}See our{" "}
            <Link to="/sms-terms" className="underline">SMS terms</Link>{" & "}
            <Link to="/privacy" className="underline">privacy policy</Link>.
          </div>
          {optedIn && user?.sms_opt_in_at && (
            <div className="mt-2 font-mono-label text-[#065F46]">
              Opted in {new Date(user.sms_opt_in_at).toLocaleDateString()}
            </div>
          )}
          {!phone && !optedIn && (
            <div className="mt-2 text-[11px] font-bold text-[#B91C1C]">
              Add a phone number in the Basics section above to enable.
            </div>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={optedIn}
          onClick={toggle}
          disabled={busy || (!optedIn && !phone)}
          data-testid="profile-sms-opt-in-toggle"
          className={`relative mt-1 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
            optedIn ? "bg-[#10B981]" : "bg-[#E5E7EB]"
          } ${busy || (!optedIn && !phone) ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              optedIn ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </Section>
  );
}


function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setOpen(false);
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-6 gb-tactile rounded-2xl border border-black/5 bg-white p-5"
      data-testid="change-password-card"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between"
        data-testid="toggle-change-password"
      >
        <div>
          <div className="font-mono-label">Security</div>
          <div className="mt-1 font-display text-lg font-bold">Change password</div>
        </div>
        <span className="font-mono-label text-[#0044FF]">
          {open ? "Close" : "Edit"}
        </span>
      </button>
      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3 border-t border-[#E5E7EB] pt-4">
          <div>
            <Label className="font-mono-label">Current password</Label>
            <Input
              data-testid="current-password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="mt-2 h-11 rounded-xl border-[#E5E7EB]"
            />
          </div>
          <div>
            <Label className="font-mono-label">New password</Label>
            <Input
              data-testid="new-password"
              type="password"
              minLength={6}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="mt-2 h-11 rounded-xl border-[#E5E7EB]"
            />
          </div>
          <Button
            data-testid="submit-change-password"
            type="submit"
            disabled={busy}
            className="h-11 w-full rounded-2xl bg-[#0044FF] text-white hover:bg-[#0036cc]"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      )}
    </div>
  );
}

function ProtectedImg({ path, className = "h-full w-full object-cover" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    (async () => {
      try {
        const res = await fetch(`${API}/files/${path}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const b = await res.blob();
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch {}
    })();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  if (!src) return <div className="h-full w-full bg-[#F0F4FF]" />;
  return <img src={src} alt="" className={className} />;
}
